/**
 * The open task list (§8.2's third section, §8.3's energy slider) — self-contained the way
 * `AreasCard` is: its own fetch of `GET /api/briefing/open` (D107), its own areas/projects
 * for display and the edit destination picker, its own hold-in-place completions
 * (`useHeldTaskActions`, shared with `StarredCard`).
 *
 * The energy slider lives inside this card rather than being its own, because it filters
 * exactly this list and nothing else (§8.3) — Starred deliberately ignores it. It reads its
 * own starting position from `GET /api/settings/energy` on mount rather than trusting a
 * page-level default, since nothing upstream fetches that anymore.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type OpenTasks, type Task } from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { useHeldTaskActions } from "../useHeldTaskActions";
import { useFireworks } from "./fireworksContext";
import { EnergySlider } from "./EnergySlider";
import { GroupedTaskList } from "./TaskList";
import { BadgeGlossary, type GlossaryEntry } from "./BadgeGlossary";
import { Calendar, Important, Search, Snooze, Star, Stopwatch } from "./icons";
import { Die } from "./Die";
import { energyTitle } from "./taskFormat";
import { todayLocalDate } from "./recurrence";

const TASK_GLOSSARY: GlossaryEntry[] = [
  {
    key: "star",
    title: "Starred",
    preview: (
      <span className="badge star">
        <Star filled /> starred
      </span>
    ),
    description: "One of today's few - chosen for right now.",
  },
  {
    key: "important",
    title: "Important",
    preview: (
      <span className="badge important">
        <Important /> important
      </span>
    ),
    description: "Matters whatever the energy slider says.",
  },
  {
    key: "energy",
    title: "Energy",
    preview: (
      <span className="badge energy">
        <Die value={5} title={energyTitle(5)} />
      </span>
    ),
    description: "Activation cost, 1 to 5 - how much this asks of you.",
  },
  {
    key: "due",
    title: "Due date",
    preview: (
      <span className="badge">
        <Calendar /> Today
      </span>
    ),
    description:
      "When it's due. Yesterday, Today, Tomorrow, and In N days / N days ago for about a " +
      "week either side, then the plain date. Red means overdue.",
  },
  {
    key: "estimate",
    title: "Estimate",
    preview: (
      <span className="badge">
        <Stopwatch /> 1h 30m
      </span>
    ),
    description: "How long you thought this would take.",
  },
  {
    key: "snoozed",
    title: "Snooze",
    preview: (
      <span className="badge">
        <Snooze /> 2026-08-10
      </span>
    ),
    description: "Hidden from the list until this date.",
  },
  {
    key: "quiet",
    title: "Quiet",
    preview: <span className="badge">5d quiet</span>,
    description: "Days since anything happened on this.",
  },
  {
    key: "seen",
    title: "Seen",
    preview: <span className="badge">seen ×3</span>,
    description: "Shown on the briefing N separate days and not yet chosen.",
  },
  {
    key: "chosen",
    title: "Chosen",
    preview: <span className="badge chosen">chosen ×4</span>,
    description: "Days you starred this for today.",
  },
  {
    key: "pushed",
    title: "Pushed",
    preview: <span className="badge">pushed ×2</span>,
    description: "Days you snoozed this away.",
  },
];

/**
 * Title, notes, area or project, case-insensitively — the same substring match the
 * blackjack slots search against titles alone, widened here since a task's own notes are
 * exactly where "was that the one about the boiler?" tends to be answered, and area/project
 * are often the term that actually comes to mind first ("wasn't there something for G&A?").
 *
 * D125 originally ran this against `data.tasks` alone — whatever the card already had loaded
 * — on the reasoning that a second, wider fetch just to search would mark everything it
 * touched "surfaced," inflating the Seen badge over nothing but typing. That held for as long
 * as the only known gap was "a task past the cap reads the same as scrolling to it already
 * would" (disclosed by the "showing X of Y" line). Two reports since (D187) found two gaps it
 * didn't cover, both invisible with nothing on screen saying so: a starred task is excluded
 * from `GET /api/briefing/open` unconditionally, cap or no cap (§8.2 — Starred has its own
 * section), and a task past the cap *or* the energy ceiling was still unreachable no matter
 * how well it matched. Both are now folded in — see `TasksCard`'s `loadStarred` and
 * `loadAllOpen` — each fetched only once a search is actually active, from endpoints that
 * (like `openTasksSnapshot`, D167) already carry no `surfaced` bookkeeping, which is what
 * keeps this from reopening D125's own objection.
 */
function matchesQuery(task: Task, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    task.title.toLowerCase().includes(q) ||
    Boolean(task.notes?.toLowerCase().includes(q)) ||
    Boolean(task.area_name?.toLowerCase().includes(q)) ||
    Boolean(task.project_name?.toLowerCase().includes(q))
  );
}

/** First occurrence wins — `allOpen` is a superset of `data.tasks` once its own fetch lands,
 * and `starred` never overlaps either (§8.2: starred and open are mutually exclusive). */
function dedupeById(tasks: Task[]): Task[] {
  const seen = new Set<string>();
  return tasks.filter((task) => {
    if (seen.has(task.id)) return false;
    seen.add(task.id);
    return true;
  });
}

export function TasksCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed a task, so this list stays current. */
  refreshKey?: number;
  /** Called after this card mutates anything, so siblings relying on the same tasks (Wins,
   * Gone-quiet, Starred) know to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects } = useAreasAndProjects(refreshKey);
  const [energy, setEnergyValue] = useState<number | null>(null);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [data, setData] = useState<OpenTasks | null>(null);
  const [starred, setStarred] = useState<Task[]>([]);
  const [allOpen, setAllOpen] = useState<Task[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const burst = useFireworks();

  const load = useCallback(
    async (overrideEnergy?: number) => {
      try {
        // Offline reads are handled centrally, in `request()` — this returns the mirrored
        // list, recomputed for this energy ceiling from the snapshot, rather than throwing
        // when the server is unreachable (D166, D169). The card does not know which it got.
        const body = await api.openTasks(overrideEnergy, showSnoozed);
        setData(body);
        setEnergyValue((current) => current ?? body.energy);
        setError(null);
      } catch (err) {
        if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [showSnoozed],
  );

  const searching = query.trim().length > 0;

  // Search-only (D187): the same request `StarredCard` already makes, fetched here too so a
  // query can match a starred task's title/notes/area/project, not just its badge on the open
  // list. `build_starred_tasks` records `surfaced` the same as the open list does — unlike
  // `snapshot`'s deliberately bookkeeping-free read, there's no such mode for this endpoint —
  // so this is gated on `searching` rather than fetched on every mount the way `load` is:
  // typing into the box is the reader actually looking for something, the same standard the
  // rest of the briefing's own `surfaced` bookkeeping already applies. Left out of `error`
  // above — this card's own list must stay visible on a failure here the same as any other
  // (D165); a search that can't reach starred tasks this moment just searches a little less
  // than it otherwise would.
  const loadStarred = useCallback(async () => {
    try {
      setStarred((await api.starredTasks()).tasks);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
    }
  }, []);

  // Search-only, same reasoning as `loadStarred` above: `data.tasks` is filtered to the
  // current energy ceiling, so a task hidden by it was invisible to `matchesQuery` no
  // matter how it matched — reported directly, with a list at "showing 16 of 29" where
  // search still couldn't find the difference. (§11 removed the separate 25-item cap this
  // used to also cite as a reason; the live read has no cap of its own worth mentioning
  // any more, so the energy ceiling is the one restriction left that still needs this.)
  // This is exactly what `openTasksSnapshot` already exists for (see its own docstring in
  // `api.ts`):
  // every open task, no ceiling, no cap, and it already carries D167's "no `surfaced`
  // bookkeeping" guarantee — the offline mirror needed that so priming it never inflates the
  // Seen badge, and a search reaching past the visible list needs the exact same guarantee for
  // the exact same reason D125 gives. `showSnoozed` is threaded through so search still
  // respects that toggle rather than overriding it.
  const loadAllOpen = useCallback(async () => {
    try {
      setAllOpen((await api.openTasksSnapshot(showSnoozed)).tasks);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
    }
  }, [showSnoozed]);

  const { actions, withHeld } = useHeldTaskActions({
    reload: useCallback(async () => {
      await load(energy ?? undefined);
      if (searching) {
        await loadStarred();
        await loadAllOpen();
      }
    }, [load, energy, loadStarred, loadAllOpen, searching]),
    onChange,
    burst,
  });

  // Refetches on a snoozed-toggle (via `load`'s own identity) and whenever `refreshKey`
  // bumps — deliberately not on every `energy` change, which fires on every drag tick and
  // would turn a slider drag into a request storm; committing reloads explicitly instead.
  useEffect(() => {
    void load(energy ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshKey]);

  // Fires once when a search starts (and again on a `refreshKey` bump, or a snoozed-toggle
  // flip via `loadAllOpen`'s own identity, while one is still active), not on every keystroke
  // — the dependency is `searching`, a boolean, not `query` itself, so typing further into an
  // already-active search doesn't refetch per character.
  useEffect(() => {
    if (searching) {
      void loadStarred();
      void loadAllOpen();
    }
  }, [searching, loadStarred, loadAllOpen, refreshKey]);

  const onEnergyCommit = useCallback(
    async (value: number) => {
      // Persisted only when the drag ends, so §8.3's event log records intent rather than
      // every intermediate value the slider passed through.
      //
      // `load` below must run no matter what happened above it — D167 already learned this
      // lesson once, for the offline case specifically: the *preference* might not save,
      // but the *filtering* is this card's own business and has to happen regardless, or
      // dragging the slider does nothing and there is no sign why. `throw`ing past `load`
      // for every *other* failure (a permissions problem, a validation error, a 500 —
      // anything that isn't `isOffline`) reopened the exact same hole D167 closed, just for
      // a different cause: `EnergySlider`'s `void onCommit(...)` discards the rejection, so
      // it never reached anywhere a reader could see it, and the list simply stopped
      // responding to the slider with nothing on screen saying why.
      let persistError: string | null = null;
      try {
        await api.setEnergy(value);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) {
          persistError = err instanceof Error ? err.message : String(err);
        }
      }
      await load(value);
      // After `load`, not before — a successful reload clears `error` on its own (see
      // `load`'s own `setError(null)`), which would otherwise erase this in the same breath
      // it was set.
      if (persistError) setError(persistError);
    },
    [load],
  );

  // A failed refetch must never take the list off the screen. This used to `return` an error
  // panel instead of the card, which meant one unreachable request — and offline, every
  // request is unreachable — replaced every task with a line of red text. The tasks were
  // never gone: `data` still held them, the card just refused to draw them. Worse, the
  // §8.5 hold-in-place rows live in that same list, so a completion's undo vanished with it
  // and the tick became unrecoverable. Every other card in the app already renders its error
  // *beside* its data; this one is now no different (DECISIONS.md D165).
  if (!data) {
    return error ? (
      <section className="panel">
        <p className="error">{error}</p>
      </section>
    ) : (
      <p className="muted">Loading…</p>
    );
  }

  // Starred and past-the-cap tasks only join the list while actively searching — unfiltered,
  // this stays exactly the open-tasks list it always was. `data.tasks` is included alongside
  // `allOpen` (rather than replaced by it) so the rows already on screen don't flash away for
  // the moment before `loadAllOpen`'s own fetch resolves; deduped by id since `allOpen` is a
  // superset of `data.tasks` once it lands (same filter, no ceiling or cap).
  const combined = searching
    ? dedupeById([...data.tasks, ...allOpen, ...starred])
    : data.tasks;
  const shown = withHeld(combined);
  const filtered = shown.filter((task) => matchesQuery(task, query));

  // Overdue and Important both bypass the energy ceiling server-side now (§11,
  // `OPEN_TASK_FILTER`) — a missed deadline or a flagged task has to be seen regardless of
  // cost, the same as it always did for Important. Splitting the render to match keeps the
  // slider from sitting above two groups it has no power over: it renders between this pair
  // and the two it actually governs, so where it sits on screen matches what it does.
  // `?? "open"` mirrors `GroupedTaskList`'s own fallback for a task with no `group` at all
  // (the starred/all-open rows folded in while searching, §8.2, never carry one).
  const isUrgent = (task: Task) => {
    const group = task.group ?? "open";
    return group === "overdue" || group === "important";
  };
  const urgent = filtered.filter(isUrgent);
  const rest = filtered.filter((task) => !isUrgent(task));

  return (
    <section className="panel">
      {error && (
        <p className="error small" style={{ marginTop: 0 }}>
          {error}
        </p>
      )}
      <header>
        <h2>Tasks</h2>
        <span className="row">
          <BadgeGlossary entries={TASK_GLOSSARY} />
        </span>
      </header>

      {/*
        Above even the slider - the same "live, no submit button" search the blackjack slots
        use to find a task by title, widened here to notes, area and project (see
        `matchesQuery`), and to starred tasks too (D187) - a task doesn't stop existing just
        because it's already chosen for today. The glass is a label for the field, not a
        button of its own, so it takes no outline and no click.
      */}
      <div className="task-search">
        <input
          className="input"
          placeholder="Search title, note, area or project - starred tasks too…"
          aria-label="Search open and starred tasks"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Search />
      </div>

      {/* Overdue and Important — never filtered by the slider below, so they render ahead
          of it, not after. No `empty` message here: nothing overdue or important just means
          this block renders nothing, same as `GroupedTaskList` already does for any group
          with zero members. */}
      <GroupedTaskList
        tasks={urgent}
        today={todayLocalDate()}
        areas={areas}
        projects={projects}
        actions={actions}
      />

      {/*
        §8.3 calls the slider the primary control. It sits between the two groups it has no
        power over (Overdue, Important, above) and the two it actually governs (Slipping,
        Everything else, below) - the same split `OPEN_TASK_FILTER` makes server-side, so
        where the slider sits on screen matches what it does. It stays inside this card
        rather than becoming one of its own, because it filters exactly these two sections
        and nothing else - the starred card deliberately ignores it too.
      */}
      <div style={{ marginBottom: "0.9rem" }}>
        <EnergySlider
          value={energy ?? data.energy}
          onChange={setEnergyValue}
          onCommit={onEnergyCommit}
        />
        {/* Scoped to what the slider actually governs (`rest`/`open_eligible_total`, both
            Overdue/Important excluded) - the old header count mixed all four groups
            together, which made "showing 25 of 29" look like the slider's own doing even
            on a page where Overdue/Important made up most of the gap. */}
        <p className="small muted" style={{ margin: "0.35rem 0 0" }}>
          {rest.length === data.open_eligible_total
            ? `${data.open_eligible_total} ${data.open_eligible_total === 1 ? "task" : "tasks"}`
            : `showing ${rest.length} of ${data.open_eligible_total}`}
        </p>
      </div>

      {data.snoozed_count > 0 && (
        <p className="small muted" style={{ margin: "0 0 0.7rem" }}>
          <Snooze /> {data.snoozed_count} snoozed{" "}
          <button
            className="btn ghost small"
            onClick={() => setShowSnoozed((v) => !v)}
            style={{ marginLeft: "0.35rem" }}
          >
            {showSnoozed ? "Hide them" : "Show them"}
          </button>
        </p>
      )}

      <GroupedTaskList
        tasks={rest}
        today={todayLocalDate()}
        areas={areas}
        projects={projects}
        actions={actions}
        empty={
          data.no_tasks
            ? "Nothing here yet."
            : query.trim()
              ? `Nothing loaded matches "${query.trim()}".`
              : "Nothing at this energy level. Push the slider up, or take the win."
        }
      />
    </section>
  );
}
